import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import axios from "axios";
import "../styles/Modal.css";

const TaskDetailsModal = ({ isOpen, onRequestClose, orderId }) => {
  const [activeTab, setActiveTab] = useState("client");
  const [taskDetails, setTaskDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [statusPickup, setStatusPickup] = useState("");
  const [statusDelivery, setStatusDelivery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [clientInfo, setClientInfo] = useState({
    nom: "",
    prenom: "",
    telephone: "",
    adresse: "",
    quartier: "",
    latitude: "",
    longitude: "",
  });

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateComment, setDuplicateComment] = useState("");

  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [refundData, setRefundData] = useState({
    quiRembourse: "Restaurant",
    aRembourser: "Client",
    montant: "",
    commentaire: "",
  });

  const modalBodyRef = useRef(null);
  const cancelButtonRef = useRef(null);

  const API_KEY = "b6c8b023-5992-4d5a-a366-265a6703c3fc";
  const EMAIL_DISPATCH = "dispatch@lalivraison.ma";

  const statusOptions = [
    "NON ASSIGNE",
    "ASSIGNE",
    "ACCEPTE",
    "START",
    "IN PROGRESS",
    "SUCCESS",
    "FAILED",
  ];

  const refundOptions = ["Restaurant", "Lalivraison", "Livreur", "Personne"];
  const refundToOptions = ["Restaurant", "Client", "Livreur", "Personne"];

  useEffect(() => {
    return () => {
      clearTimeout(window.successMessageTimeout);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !orderId) {
      return;
    }

    const fetchTaskDetails = async () => {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      try {
        const params = new URLSearchParams();
        params.append("order_id", orderId);
        params.append("api_key", API_KEY);
        params.append("email_dispatch", EMAIL_DISPATCH);

        const response = await axios.post(
          "https://lalivraison.ma/Livreurxcs/Dispatch/getDetailsCommande.php",
          params,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            timeout: 10000,
          }
        );

        if (typeof response.data === "string" && response.data.startsWith("<!DOCTYPE")) {
          throw new Error("Received HTML instead of JSON. Please check the API endpoint or parameters.");
        }

        if (response.data && typeof response.data === "object") {
          setTaskDetails(response.data);
          setStatusPickup(response.data.informations_commande?.status_pickup || "NON ASSIGNE");
          setStatusDelivery(response.data.informations_commande?.status_delivery || "NON ASSIGNE");
          setClientInfo({
            nom: response.data.informations_client?.nom || "",
            prenom: response.data.informations_client?.prenom || "",
            telephone: response.data.informations_client?.telephone || "",
            adresse: response.data.informations_delivery?.adresse || "",
            quartier: response.data.informations_delivery?.quartier || "",
            latitude: response.data.informations_delivery?.latitude || "",
            longitude: response.data.informations_delivery?.longitude || "",
          });
        } else {
          throw new Error("Unexpected response format. Expected a JSON object.");
        }
      } catch (err) {
        setError(
          err.code === "ERR_NETWORK"
            ? "Network error: Unable to connect to the server."
            : err.code === "ECONNABORTED"
            ? "Request timed out: The server took too long to respond."
            : "Failed to fetch task details: " + err.message
        );
        console.error("Task Details API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskDetails();
  }, [isOpen, orderId]);

  const groupFormuleByTitre = (formule) => {
    if (!Array.isArray(formule)) return {};
    return formule.reduce((acc, item) => {
      const titre = item.titre || "Uncategorized";
      if (!acc[titre]) acc[titre] = [];
      acc[titre].push(item);
      return acc;
    }, {});
  };

  const handleStatusUpdate = async (newStatus, type) => {
    const pickdeliv = type === "pickup" ? "status_pickup" : "status_delivery";
    const commentairefailed = newStatus === "FAILED" ? "Status updated to FAILED" : "";

    try {
      const params = new URLSearchParams();
      params.append("order_id", orderId);
      params.append("status", newStatus);
      params.append("pickdeliv", pickdeliv);
      params.append("commentairefailed", commentairefailed);
      params.append("email_dispatch", EMAIL_DISPATCH);
      params.append("api_key", API_KEY);

      console.log("Sending status update with payload:", params.toString());

      const response = await axios.post(
        "https://lalivraison.ma/Livreurxcs/Dispatch/updatecommandev2.php",
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          timeout: 30000,
        }
      );

      console.log("Status Update Response:", response.data);

      if (typeof response.data === "string" && response.data.includes("<!DOCTYPE")) {
        throw new Error("Received HTML instead of JSON. Possible server error.");
      }

      if (response.data.success) {
        if (type === "pickup") {
          setStatusPickup(newStatus);
          setTaskDetails((prev) => ({
            ...prev,
            informations_commande: {
              ...prev.informations_commande,
              status_pickup: newStatus,
            },
          }));
        } else {
          setStatusDelivery(newStatus);
          setTaskDetails((prev) => ({
            ...prev,
            informations_commande: {
              ...prev.informations_commande,
              status_delivery: newStatus,
            },
          }));
        }
        setError(null);
        setSuccessMessage("Status updated successfully");

        // Scroll to the top to ensure the success message is visible
        if (modalBodyRef.current) {
          modalBodyRef.current.scrollTo({ top: 0, behavior: "auto" });
        }

        // Clear any existing timeout
        clearTimeout(window.successMessageTimeout);
        // Set timeout to clear success message and close modal
        window.successMessageTimeout = setTimeout(() => {
          setSuccessMessage(null);
          onRequestClose(); // Close the modal
        }, 2000); // 2 seconds delay to show the success message
      } else if (response.data.message.includes("No rows updated")) {
        // Re-fetch task details to verify if the update was applied
        console.warn("No rows updated, re-fetching task details:", response.data.message);
        const params = new URLSearchParams();
        params.append("order_id", orderId);
        params.append("api_key", API_KEY);
        params.append("email_dispatch", EMAIL_DISPATCH);

        const detailsResponse = await axios.post(
          "https://lalivraison.ma/Livreurxcs/Dispatch/getDetailsCommande.php",
          params,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            timeout: 10000,
          }
        );

        if (detailsResponse.data?.informations_commande?.[pickdeliv] === newStatus) {
          if (type === "pickup") {
            setStatusPickup(newStatus);
            setTaskDetails((prev) => ({
              ...prev,
              informations_commande: {
                ...prev.informations_commande,
                status_pickup: newStatus,
              },
            }));
          } else {
            setStatusDelivery(newStatus);
            setTaskDetails((prev) => ({
              ...prev,
              informations_commande: {
                ...prev.informations_commande,
                status_delivery: newStatus,
              },
            }));
          }
          setError(null);
          setSuccessMessage("Status updated successfully");

          // Scroll to the top to ensure the success message is visible
          if (modalBodyRef.current) {
            modalBodyRef.current.scrollTo({ top: 0, behavior: "auto" });
          }

          // Clear any existing timeout
          clearTimeout(window.successMessageTimeout);
          // Set timeout to clear success message and close modal
          window.successMessageTimeout = setTimeout(() => {
            setSuccessMessage(null);
            onRequestClose(); // Close the modal
          }, 2000); // 2 seconds delay to show the success message
        } else {
          throw new Error(response.data.message || "Failed to update status.");
        }
      } else {
        throw new Error(response.data.message || "Failed to update status.");
      }
    } catch (err) {
      console.error("Status Update API Error:", err);
      setError("Failed to update status: " + err.message);
      setSuccessMessage(null);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setSuccessMessage(null);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setSuccessMessage(null);
    setClientInfo({
      nom: taskDetails.informations_client?.nom || "",
      prenom: taskDetails.informations_client?.prenom || "",
      telephone: taskDetails.informations_client?.telephone || "",
      adresse: taskDetails.informations_delivery?.adresse || "",
      quartier: taskDetails.informations_delivery?.quartier || "",
      latitude: taskDetails.informations_delivery?.latitude || "",
      longitude: taskDetails.informations_delivery?.longitude || "",
    });
  };

  const handleClientInfoChange = (field, value) => {
    setClientInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClientInfoUpdate = async () => {
    try {
      const params = new URLSearchParams();
      params.append("order_id", orderId);
      params.append("nomcl", clientInfo.nom);
      params.append("prenomcl", clientInfo.prenom);
      params.append("tel", clientInfo.telephone);
      params.append("adrss", clientInfo.adresse);
      params.append("quartier", clientInfo.quartier || "");
      params.append("lat", clientInfo.latitude);
      params.append("lng", clientInfo.longitude);
      params.append("email_dispatch", EMAIL_DISPATCH);
      params.append("api_key", API_KEY);

      console.log("Sending client info update with payload:", params.toString());

      const response = await axios.post(
        "https://lalivraison.ma/Livreurxcs/Dispatch/updateinfo.php",
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          timeout: 30000,
        }
      );

      console.log("Client Info Update Response:", response.data);

      if (typeof response.data === "string" && response.data.includes("<!DOCTYPE")) {
        throw new Error("Received HTML instead of JSON. Possible server error.");
      }

      if (response.data.success) {
        setTaskDetails((prev) => ({
          ...prev,
          informations_client: {
            ...prev.informations_client,
            nom: clientInfo.nom,
            prenom: clientInfo.prenom,
            telephone: clientInfo.telephone,
          },
          informations_delivery: {
            ...prev.informations_delivery,
            adresse: clientInfo.adresse,
            quartier: clientInfo.quartier,
            latitude: clientInfo.latitude,
            longitude: clientInfo.longitude,
          },
        }));
        setError(null);
        setSuccessMessage("Information updated successfully");

        setTimeout(() => {
          if (cancelButtonRef.current) {
            cancelButtonRef.current.click();
          }
          if (modalBodyRef.current) {
            modalBodyRef.current.scrollTo({ top: 0, behavior: "auto" });
          }
        }, 50);

        clearTimeout(window.successMessageTimeout);
        window.successMessageTimeout = setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      } else {
        throw new Error(response.data.message || "Failed to update client information.");
      }
    } catch (err) {
      console.error("Client Info Update API Error:", err);
      setError("Failed to update client information: " + err.message);
      setSuccessMessage(null);
    }
  };

  const handleDuplicateOrder = async () => {
    setIsDuplicating(true);
    try {
      const params = new URLSearchParams();
      params.append("orderid", orderId);
      params.append("commentairedub", duplicateComment);
      params.append("email_dispatch", EMAIL_DISPATCH);
      params.append("api_key", API_KEY);

      console.log("Sending duplicate order payload:", params.toString());

      const response = await axios.post(
        "https://lalivraison.ma/Livreurxcs/Dispatch/createduplicate.php",
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          timeout: 10000,
        }
      );

      console.log("Duplicate Order Response:", response.data);

      if (response.data.success) {
        setSuccessMessage(response.data.message || "Order duplicated successfully");
        setIsDuplicateModalOpen(false);
        setDuplicateComment("");

        clearTimeout(window.successMessageTimeout);
        window.successMessageTimeout = setTimeout(() => {
          setSuccessMessage(null);
        }, 3500);
      } else {
        throw new Error(response.data.message || "Failed to duplicate order.");
      }
    } catch (err) {
      console.error("Duplicate Order API Error:", err);
      setError("Failed to duplicate order: " + err.message);
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleRefundOrder = async () => {
    if (!refundData.montant || parseFloat(refundData.montant) <= 0) {
      setError("Please enter a valid refund amount");
      return;
    }
    setIsRefunding(true);
    try {
      const params = new URLSearchParams();
      params.append("order_id", orderId);
      params.append("montant_refund", refundData.montant);
      params.append("commentaire", refundData.commentaire);
      params.append("rembouressement", refundData.aRembourser);
      params.append("quirembourse", refundData.quiRembourse);
      params.append("email_dispatch", EMAIL_DISPATCH);
      params.append("api_key", API_KEY);

      console.log("Sending refund order payload:", params.toString());

      const response = await axios.post(
        "https://lalivraison.ma/Livreurxcs/Dispatch/rembouressement.php",
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          timeout: 10000,
        }
      );

      console.log("Refund Order Response:", response.data);

      if (response.data.success) {
        setSuccessMessage(response.data.message || "Refund processed successfully");
        setIsRefundModalOpen(false);
        setRefundData({
          quiRembourse: "Restaurant",
          aRembourser: "Client",
          montant: "",
          commentaire: "",
        });

        clearTimeout(window.successMessageTimeout);
        window.successMessageTimeout = setTimeout(() => {
          setSuccessMessage(null);
        }, 3500);
      } else {
        throw new Error(response.data.message || "Failed to process refund.");
      }
    } catch (err) {
      console.error("Refund Order API Error:", err);
      setError("Failed to process refund: " + err.message);
    } finally {
      setIsRefunding(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div className="modal-content">
      <div className="modal-header">
        <h5>Order ID: {orderId}</h5>
        <div className="header-buttons">
          <button className="refund-button" onClick={() => setIsRefundModalOpen(true)}>
            Refund
          </button>
          <button className="duplicate-button" onClick={() => setIsDuplicateModalOpen(true)}>
            Duplicate
          </button>
          <button className="close-button" onClick={onRequestClose}>×</button>
        </div>
      </div>
      <div className="modal-body" ref={modalBodyRef}>
        {loading && <div className="modal-loading">Loading...</div>}
        {error && <div className="modal-error">{error}</div>}
        {successMessage && <div className="modal-success">{successMessage}</div>}
        {taskDetails ? (
          <section className="filters3">
            <ul className="filters-content3">
              <button
                className={`filters__button3 ${activeTab === "client" ? "is-active" : ""}`}
                onClick={() => setActiveTab("client")}
              >
                Information Client
              </button>
              <button
                className={`filters__button3 ${activeTab === "commande" ? "is-active" : ""}`}
                onClick={() => setActiveTab("commande")}
              >
                Information Commande
              </button>
              <button
                className={`filters__button3 ${activeTab === "history" ? "is-active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                History
              </button>
            </ul>
            <div>
              <div className={`tab-content ${activeTab === "client" ? "is-active" : ""}`}>
                {!isEditing && (
                  <div className="edit-section">
                    <button className="edit-button" onClick={startEditing}>
                      <i className="fas fa-edit"></i>
                    </button>
                  </div>
                )}
                <div className="modal-info">
                  <i className="fas fa-user"></i>
                  <span>
                    Nom: {isEditing ? (
                      <input
                        type="text"
                        value={clientInfo.nom}
                        onChange={(e) => handleClientInfoChange("nom", e.target.value)}
                        placeholder="Enter name"
                      />
                    ) : (
                      `${taskDetails.informations_client?.nom || "N/A"}`
                    )}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-user"></i>
                  <span>
                    Prénom: {isEditing ? (
                      <input
                        type="text"
                        value={clientInfo.prenom}
                        onChange={(e) => handleClientInfoChange("prenom", e.target.value)}
                        placeholder="Enter surname"
                      />
                    ) : (
                      `${taskDetails.informations_client?.prenom || "N/A"}`
                    )}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-phone"></i>
                  <span>
                    Telephone: {isEditing ? (
                      <input
                        type="text"
                        value={clientInfo.telephone}
                        onChange={(e) => handleClientInfoChange("telephone", e.target.value)}
                        placeholder="Enter phone number"
                      />
                    ) : (
                      `${taskDetails.informations_client?.telephone || "N/A"}`
                    )}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>
                    Adresse: {isEditing ? (
                      <input
                        type="text"
                        value={clientInfo.adresse}
                        onChange={(e) => handleClientInfoChange("adresse", e.target.value)}
                        placeholder="Enter address"
                      />
                    ) : (
                      `${taskDetails.informations_delivery?.adresse || "N/A"}`
                    )}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-signs"></i>
                  <span>
                    Quartier: {isEditing ? (
                      <input
                        type="text"
                        value={clientInfo.quartier}
                        onChange={(e) => handleClientInfoChange("quartier", e.target.value)}
                        placeholder="Enter quartier"
                      />
                    ) : (
                      `${taskDetails.informations_delivery?.quartier || "N/A"}`
                    )}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-pin"></i>
                  <span>
                    Latitude: {isEditing ? (
                      <input
                        type="text"
                        value={clientInfo.latitude}
                        onChange={(e) => handleClientInfoChange("latitude", e.target.value)}
                        placeholder="Enter latitude"
                      />
                    ) : (
                      `${taskDetails.informations_delivery?.latitude || "N/A"}`
                    )}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-pin"></i>
                  <span>
                    Longitude: {isEditing ? (
                      <input
                        type="text"
                        value={clientInfo.longitude}
                        onChange={(e) => handleClientInfoChange("longitude", e.target.value)}
                        placeholder="Enter longitude"
                      />
                    ) : (
                      `${taskDetails.informations_delivery?.longitude || "N/A"}`
                    )}
                  </span>
                </div>
                {isEditing && (
                  <div className="edit-section">
                    <button className="update-button" onClick={handleClientInfoUpdate}>
                      Update
                    </button>
                    <button
                      className="cancel-button"
                      ref={cancelButtonRef}
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div className={`tab-content ${activeTab === "commande" ? "is-active" : ""}`}>
                <div className="modal-info">
                  <i className="fas fa-hashtag"></i>
                  <span>Commande ID: {taskDetails.informations_commande?.id || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-money-bill-wave"></i>
                  <span>Prix Total: {taskDetails.informations_commande?.prix_total_a_restaurant || "N/A"} MAD</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-store"></i>
                  <span>Restaurant: {taskDetails.informations_pickup?.designation || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-phone"></i>
                  <span>
                    Telephone Restaurant: {taskDetails.informations_pickup?.num_telephone || "N/A"}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Adresse Pickup: {taskDetails.informations_pickup?.adresse || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-user-check"></i>
                  <span>
                    Livreur: {taskDetails.informations_commande?.livreur_affecte || "Not Assigned"}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-credit-card"></i>
                  <span>
                    Moyen Paiement: {taskDetails.informations_commande?.moyen_paiement || "N/A"}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-check-circle"></i>
                  <span>
                    Status Pickup:
                    <select
                      value={statusPickup}
                      onChange={(e) => handleStatusUpdate(e.target.value, "pickup")}
                    >
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-truck"></i>
                  <span>
                    Status Delivery:
                    <select
                      value={statusDelivery}
                      onChange={(e) => handleStatusUpdate(e.target.value, "delivery")}
                    >
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-comment"></i>
                  <span>Commentaire: {taskDetails.informations_commande?.note || "N/A"}</span>
                </div>
                {taskDetails.details_commande && taskDetails.details_commande.length > 0 && (
                  <div className="modal-info">
                    <i className="fas fa-shopping-cart"></i>
                    <span>
                      Articles:
                      {taskDetails.details_commande.map((item, index) => (
                        <div key={index} className="order-item">
                          <div className="order-item-header">
                            <strong>{item.designation || "N/A"}</strong> (Quantité: {item.quantite || "N/A"}, Prix Unitaire: {item.prix_unitaire || "N/A"} MAD)
                          </div>
                          {item.formule && item.formule.length > 0 && (
                            <div className="formule-details">
                              {Object.entries(groupFormuleByTitre(item.formule)).map(([titre, items]) => (
                                <div key={titre} className="formule-section">
                                  <h4>{titre}</h4>
                                  <ul>
                                    {items.map((formuleItem, idx) => (
                                      <li key={idx}>
                                        {formuleItem.label || "N/A"} (Quantité: {formuleItem.quantite || "N/A"}, Prix: {formuleItem.prix || "0.00"} MAD)
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </span>
                  </div>
                )}
              </div>
              <div className={`tab-content ${activeTab === "history" ? "is-active" : ""}`}>
                <div className="modal-info">
                  <i className="fas fa-clock"></i>
                  <span>Date Création: {taskDetails.informations_commande?.created_at || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-store"></i>
                  <span>designation: {taskDetails.informations_pickup?.designation || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-phone"></i>
                  <span>num_telephone restaurant: {taskDetails.informations_pickup?.num_telephone || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>adresse restaurant: {taskDetails.informations_pickup?.adresse || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-check-circle"></i>
                  <span>status_pickup: {taskDetails.informations_commande?.status_pickup || "N/A"}</span>
                </div>
                <hr style={{ border: "3px solid gray", margin: "5px 0" }} />
                <div className="modal-info">
                  <i className="fas fa-map-signs"></i>
                  <span>quartier client: {taskDetails.informations_delivery?.quartier || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>
                    adresse: {(taskDetails.informations_delivery?.adresse || "") + " " + (taskDetails.informations_delivery?.additionnel || "") || "N/A"}
                  </span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-pin"></i>
                  <span>latitude: {taskDetails.informations_delivery?.latitude || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-map-pin"></i>
                  <span>longitude: {taskDetails.informations_delivery?.longitude || "N/A"}</span>
                </div>
                <div className="modal-info">
                  <i className="fas fa-truck"></i>
                  <span>status_delivery: {taskDetails.informations_commande?.status_delivery || "N/A"}</span>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <div className="modal-placeholder">No data available</div>
        )}
      </div>
    </div>
  );

  const duplicateModalContent = (
    <div className="modal-content duplicate-modal">
      <div className="modal-header">
        <h5>Duplicate Order</h5>
        <button className="close-button" onClick={() => setIsDuplicateModalOpen(false)}>×</button>
      </div>
      <div className="modal-body">
        <div className="modal-info">
          <label>Commentaire:</label>
          <textarea
            value={duplicateComment}
            onChange={(e) => setDuplicateComment(e.target.value)}
            placeholder="Enter comment"
            rows="3"
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #E2E8F0" }}
          />
        </div>
        <div className="edit-section">
          <button
            className="update-button"
            onClick={handleDuplicateOrder}
            disabled={isDuplicating}
          >
            {isDuplicating ? "Duplicating..." : "Duplicate"}
          </button>
        </div>
      </div>
    </div>
  );

  const refundModalContent = (
    <div className="modal-content refund-modal">
      <div className="modal-header">
        <h5>Refund Order</h5>
        <button className="close-button" onClick={() => setIsRefundModalOpen(false)}>×</button>
      </div>
      <div className="modal-body">
        <div className="modal-info">
          <label>Qui Rembourse:</label>
          <select
            value={refundData.quiRembourse}
            onChange={(e) => setRefundData({ ...refundData, quiRembourse: e.target.value })}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #E2E8F0" }}
          >
            {refundOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="modal-info">
          <label>À Rembourser:</label>
          <select
            value={refundData.aRembourser}
            onChange={(e) => setRefundData({ ...refundData, aRembourser: e.target.value })}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #E2E8F0" }}
          >
            {refundToOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="modal-info">
          <label>Montant à Rembourser:</label>
          <input
            type="number"
            value={refundData.montant}
            onChange={(e) => setRefundData({ ...refundData, montant: e.target.value })}
            placeholder="Enter amount"
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #E2E8F0" }}
          />
        </div>
        <div className="modal-info">
          <label>Commentaire:</label>
          <textarea
            value={refundData.commentaire}
            onChange={(e) => setRefundData({ ...refundData, commentaire: e.target.value })}
            placeholder="Enter comment"
            rows="3"
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #E2E8F0" }}
          />
        </div>
        <div className="edit-section">
          <button
            className="update-button"
            onClick={handleRefundOrder}
            disabled={isRefunding}
          >
            {isRefunding ? "Processing..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        className="modal"
        overlayClassName="modal-overlay"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        parentSelector={() => document.getElementById("modal-root") || document.body}
      >
        {modalContent}
      </Modal>

      <Modal
        isOpen={isDuplicateModalOpen}
        onRequestClose={() => setIsDuplicateModalOpen(false)}
        className="modal duplicate-modal"
        overlayClassName="modal-overlay"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        parentSelector={() => document.getElementById("modal-root") || document.body}
      >
        {duplicateModalContent}
      </Modal>

      <Modal
        isOpen={isRefundModalOpen}
        onRequestClose={() => setIsRefundModalOpen(false)}
        className="modal refund-modal"
        overlayClassName="modal-overlay"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        parentSelector={() => document.getElementById("modal-root") || document.body}
      >
        {refundModalContent}
      </Modal>
    </>
  );
};

export default TaskDetailsModal;